import { isSkorSdkMode } from "../../helper/skorSdk";
import React, { useContext, useState } from "react";
import { Modal } from "react-bootstrap";
import useTranslator from "../../hooks/useTranslator";
import { formatPropertyAddress, getUserDisplayName } from "../../helper/common";
import InsuranceShieldIcon from "./InsuranceShieldIcon";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";
import SkorProtectShield from "../../assets/img/skor-protect-shield.png";
import AuthContext from "../../context/auth/AuthContext";
import constants from "../../Utils/constants";
import { DownloadIcon } from "../../helper/svgHelper";
import { Button, Icon } from "../design";
import "./AwardInsuranceConfirmPopup.css";

const AwardInsuranceConfirmPopup = ({
  show,
  onHide,
  onConfirm,
  selectedTenant,
  property,
  currentLang,
  isInsurable,
  errorStatus,
  errorCode,
  errorCodes,
  errorMessage,
  validationMessages,
  insuranceRetryLoading,
  pricingPreview,
  preFilledInsuranceDocument,
  onRetryInsurance,
  skorProtectLoading,
}) => {
  const { _t } = useTranslator();
  const { user } = useContext(AuthContext);
  const [selection, setSelection] = useState(null); // null | "insurance" | "skor_protect"
  const [isConfirming, setIsConfirming] = useState(false);

  const renderInsurancePricing = (preview) => {
    const total = Number(preview?.total);
    if (!Number.isFinite(total)) return null;

    return (
      <span className="aicp-insurance-price">
        {total.toFixed(2)} €<span>/{_t("year")}</span>
      </span>
    );
  };

  const renderInsuranceCoverage = () =>
    preFilledInsuranceDocument ? (
      <a
        href={`${preFilledInsuranceDocument}`}
        target="_blank"
        rel="noopener noreferrer"
        className="aicp-insurance-coverage-link"
        onClick={(event) => event.stopPropagation()}
      >
        <DownloadIcon />
        {_t("insuranceCoverage")}
      </a>
    ) : (
      <span className="aicp-card-payer">{_t("paidByLandlord")}</span>
    );

  const propertyName = formatPropertyAddress(property, currentLang);
  const resolvedErrorCodes = errorCodes?.length ? errorCodes : [errorCode];
  const isMissingVerifiedDocumentNumber = resolvedErrorCodes.includes(
    constants.INSURANCE_ERROR_CODE.MISSING_VERIFIED_DOCUMENT_NUMBER
  );
  const isMissingVerifiedName = resolvedErrorCodes.includes(
    constants.INSURANCE_ERROR_CODE.MISSING_VERIFIED_NAME
  );
  const isMdpNameValidationError = resolvedErrorCodes.includes(
    constants.INSURANCE_ERROR_CODE.MDP_NAME_VALIDATION_ERROR
  );
  const verifiedIdentityValidationMessages = Array.from(
    new Set(
      resolvedErrorCodes
        .map((code, index) => {
          if (validationMessages?.[index]) return validationMessages[index];

          if (
            code ===
            constants.INSURANCE_ERROR_CODE.MISSING_VERIFIED_DOCUMENT_NUMBER
          ) {
            return _t("oneOrMoreTenantsMissingVerifiedDocumentNumber");
          }

          if (code === constants.INSURANCE_ERROR_CODE.MISSING_VERIFIED_NAME) {
            return _t("oneOrMoreTenantsMissingVerifiedName");
          }

          if (
            code === constants.INSURANCE_ERROR_CODE.MDP_NAME_VALIDATION_ERROR
          ) {
            return _t(
              "oneOrMoreTenantOrGuarantorsHaveNameShorterThanTwoCharacters"
            );
          }

          return null;
        })
        .filter(Boolean)
    )
  );

  const specificInsuranceError = (() => {
    if (
      isMissingVerifiedDocumentNumber ||
      isMissingVerifiedName ||
      isMdpNameValidationError
    ) {
      return {
        icon: "/images/warning-icon.svg",
        iconClassName: "aic-status-banner-warning-icon",
        title: _t("couldNotCheckInsuranceRightNow"),
        descriptions: verifiedIdentityValidationMessages.length
          ? verifiedIdentityValidationMessages
          : [errorMessage].filter(Boolean),
        hint: _t("awardNowAndAddInsuranceLater"),
      };
    }
    if (
      ["NIVEL3_ERR010", "NIVEL3_ERR016", "NIVEL3_ERR022"].includes(errorCode)
    ) {
      return {
        icon: "/images/insurance-not-available.svg",
        title: _t("insuranceUnavailableForThisRent"),
        description: _t("insuranceUnavailableForThisRentDescription"),
        hint: _t("awardWithoutProtectionOrLowerRent"),
      };
    }
    if (["NIVEL3_ERR043", "NIVEL3_ERR053"].includes(errorCode)) {
      return {
        icon: "/images/property-already-insured.svg",
        title: _t("propertyAlreadyInsured"),
        description: _t("propertyAlreadyInsuredDescription"),
        hint: _t("awardWithoutProtectionExistingPolicyHint"),
      };
    }
    if (["NIVEL3_ERR085", "NIVEL3_ERR093"].includes(errorCode)) {
      return {
        icon: "/images/tenant-already-request.svg",
        title: _t("tenantAlreadyInAnotherInsuranceRequest"),
        description: _t("tenantAlreadyInAnotherInsuranceRequestDescription"),
        hint: _t("awardWithoutProtectionDuplicateRequestHint"),
      };
    }
    return null;
  })();

  const tenantOptedInSkorProtect = selectedTenant?.some(
    (t) => t?.tenantid?.skor_protect || t?.application_id?.skor_protect
  );

  const handleSelect = (type) => {
    setSelection((prev) => (prev === type ? null : type));
  };

  const handleConfirm = async () => {
    if (isConfirming) return;

    const decision =
      selection === "insurance"
        ? "insurance"
        : selection === "skor_protect"
          ? "skor_protect_flow"
          : "direct_award";

    setIsConfirming(true);
    try {
      const shouldClose = await onConfirm(decision);
      if (shouldClose !== false) {
        onHide();
      }
    } finally {
      setIsConfirming(false);
    }
  };

  const tenants = selectedTenant?.flatMap((s) => {
    const isApplication = s?.application_id?.participants?.length > 0;
    return isApplication
      ? s?.application_id?.participants?.map((p) => p?.tenant_info)
      : s?.tenantid;
  });

  return (
    <Modal
      show={show}
      onHide={() => {
        if (!isConfirming) onHide();
      }}
      centered
      backdrop={isConfirming ? "static" : true}
      keyboard={!isConfirming}
      className="newmodalui wrapper-modal inviteTenantModal max-w-lg awardInsuranceConfirmModal"
    >
      <Modal.Header>
        <h2 className="aic-title">{_t("awardProperty")}</h2>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className="topRightIcon"
          aria-label={_t("close")}
          iconStart={<Icon name="close" size={16} />}
          onClick={() => {
            if (!isConfirming) onHide();
          }}
        />
      </Modal.Header>
      <Modal.Body>
        <p className="aic-desc">
          {_t("youAreAboutToAward")}{" "}
          <span className="aic-prop-name">{propertyName}</span>{" "}
          {_t("toTheFollowingTenants")}:
        </p>

        {tenants?.length > 0 && (
          <div className="aic-chips">
            {tenants?.map((tenant, idx) => (
              <span key={idx} className="aic-chip">
                {getUserDisplayName(tenant) || tenant?.tenantid?.label || ""}
              </span>
            ))}
          </div>
        )}

        {!isInsurable &&
          errorStatus === constants.INSURANCE_ERROR_STATUS.ERROR && (
            <>
              <p className="aicp-section-label">
                {_t("protectYourRental")}{" "}
                <span className="aicp-optional">({_t("optional")})</span>
              </p>

              {specificInsuranceError ? (
                <>
                  <div className="aic-status-banner aic-status-banner--specific">
                    <img
                      src={specificInsuranceError.icon}
                      className={specificInsuranceError.iconClassName || ""}
                      alt=""
                    />
                    <div className="aic-status-banner-text">
                      <p className="aic-status-banner-title">
                        {specificInsuranceError.title}
                      </p>
                      {(
                        specificInsuranceError.descriptions || [
                          specificInsuranceError.description,
                        ]
                      ).map((description) => (
                        <p key={description} className="aic-status-banner-desc">
                          {description}
                        </p>
                      ))}
                    </div>
                  </div>
                  {!isSkorSdkMode() && (
                    <p className="aic-support-line">
                      <Icon
                        name="message"
                        size={14}
                        className="aicp-support-icon"
                      />
                      {_t("needHelp")}{" "}
                      <a
                        href="https://wa.me/34617662602"
                        className="aic-support-link"
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                        target="_blank"
                      >
                        {_t("contactSupport")}
                      </a>
                    </p>
                  )}
                  <p className="aicp-hint">{specificInsuranceError.hint}</p>
                </>
              ) : (
                <>
                  <div className="aic-status-banner aic-status-banner--neutral">
                    {insuranceRetryLoading ? (
                      <div className="aic-skeleton aic-skeleton-icon" />
                    ) : (
                      <img src="/images/could-not-check-insurance.svg" alt="" />
                    )}
                    <div className="aic-status-banner-text">
                      {insuranceRetryLoading ? (
                        <>
                          <div className="aic-skeleton aic-skeleton-title" />
                          <div className="aic-skeleton aic-skeleton-desc" />
                        </>
                      ) : (
                        <>
                          <p className="aic-status-banner-title">
                            {_t("couldNotCheckInsuranceRightNow")}
                          </p>
                          <p className="aic-status-banner-desc">
                            {_t("genericInsuranceCheckError")}
                          </p>
                        </>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        className="aicp-retry"
                        loading={insuranceRetryLoading}
                        iconStart={<Icon name="refresh" size={16} />}
                        onClick={onRetryInsurance}
                      >
                        {_t("tryAgain")}
                      </Button>
                    </div>
                  </div>
                  {!insuranceRetryLoading && (
                    <p className="aicp-hint">
                      {_t("awardNowAndAddInsuranceLater")}
                    </p>
                  )}
                </>
              )}
            </>
          )}

        {isInsurable &&
          (tenantOptedInSkorProtect ||
          user?.current_workspace?.skor_protect_service === true ? (
            <>
              <p className="aicp-section-label">
                {_t("protectYourRental")}{" "}
                <span className="aicp-optional">({_t("optional")})</span>
              </p>

              <div className="aicp-cards-row">
                {/* Insurance card */}

                <div
                  className={`aicp-card${selection === "insurance" ? " aicp-card--ins-selected" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelect("insurance")}
                  onKeyDown={(event) => {
                    if (
                      event.target === event.currentTarget &&
                      (event.key === "Enter" || event.key === " ")
                    ) {
                      event.preventDefault();
                      handleSelect("insurance");
                    }
                  }}
                >
                  <div className="aicp-card-header">
                    <div className="aicp-card-icon-title">
                      <InsuranceShieldIcon size={18} color={"#AB8D5C"} />
                      <span className="aicp-card-title">{_t("insurance")}</span>
                    </div>
                    <div
                      className={`aicp-radio${selection === "insurance" ? " aicp-radio--ins" : ""}`}
                    />
                  </div>
                  <p className="aicp-card-desc">
                    {_t("insuranceCardDescription")}
                  </p>
                  <div className="aicp-card-footer-row aicp-insurance-price-row">
                    {renderInsuranceCoverage()}
                    {renderInsurancePricing(pricingPreview?.insurance)}
                  </div>
                </div>

                {/* Skor Protect card */}
                <div
                  className={`aicp-card aicp-card-sp${selection === "skor_protect" ? " aicp-card--sp-selected" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelect("skor_protect")}
                  onKeyDown={(event) => {
                    if (
                      event.target === event.currentTarget &&
                      (event.key === "Enter" || event.key === " ")
                    ) {
                      event.preventDefault();
                      handleSelect("skor_protect");
                    }
                  }}
                >
                  <div className="aicp-card-header">
                    <div className="aicp-card-icon-title">
                      <img
                        src={SkorProtectShield}
                        alt=""
                        className="sp-shield"
                      />
                      <span className="aicp-card-title">
                        {_t("skorProtectTitle")}
                      </span>
                    </div>
                    <div
                      className={`aicp-radio${selection === "skor_protect" ? " aicp-radio--sp" : ""}`}
                    />
                  </div>
                  <p className="aicp-card-desc">
                    {_t("skorProtectCardDescription")}
                  </p>
                  <div className="aicp-card-footer-row">
                    <div className="aicp-card-footer-info">
                      <span className="aicp-card-payer">
                        {_t("paidByTenant")}
                      </span>
                      <a
                        href="https://www.skortorent.com/inquilino/skor-protect/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aicp-more-info-link"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {_t("moreInformation")} →
                      </a>
                    </div>
                    {tenantOptedInSkorProtect && (
                      <span className="aicp-tenant-opted-badge">
                        {_t("tenantOptedIn")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* What happens next — Insurance */}
              {selection === "insurance" && (
                <div className="aicp-next-box aicp-next-box--ins">
                  <p className="aicp-next-label">{_t("whatHappensNext")}</p>
                  <ol className="aicp-next-steps">
                    <li>
                      <span className="aicp-step-num aicp-step-num--ins">
                        1
                      </span>
                      {_t("insuranceAwardStep1")}
                    </li>
                    <li>
                      <span className="aicp-step-num aicp-step-num--ins">
                        2
                      </span>
                      {_t("insuranceAwardStep2")}
                    </li>
                    <li>
                      <span className="aicp-step-num aicp-step-num--ins">
                        3
                      </span>
                      {_t("insuranceAwardStep3")}
                    </li>
                  </ol>
                </div>
              )}

              {/* What happens next — Skor Protect */}
              {selection === "skor_protect" && (
                <div className="aicp-next-box aicp-next-box--sp">
                  <p className="aicp-next-label">{_t("whatHappensNext")}</p>
                  <ol className="aicp-next-steps">
                    <li>
                      <span className="aicp-step-num aicp-step-num--sp">1</span>
                      {_t("skorProtectAwardStep1")}
                    </li>
                    <li>
                      <span className="aicp-step-num aicp-step-num--sp">2</span>
                      {_t("skorProtectAwardStep2")}
                    </li>
                    <li>
                      <span className="aicp-step-num aicp-step-num--sp">3</span>
                      {_t("skorProtectAwardStep3")}
                    </li>
                  </ol>
                </div>
              )}

              <p className="aicp-hint">{_t("selectOneOrLeaveUnselected")}</p>
            </>
          ) : (
            <>
              <p className="aicp-section-label">
                {_t("protectYourRental")}{" "}
                <span className="aicp-optional">({_t("optional")})</span>
              </p>

              <div className="aicp-cards-row aicp-cards-row--single">
                <div
                  className={`aicp-card${selection === "insurance" ? " aicp-card--ins-selected" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelect("insurance")}
                  onKeyDown={(event) => {
                    if (
                      event.target === event.currentTarget &&
                      (event.key === "Enter" || event.key === " ")
                    ) {
                      event.preventDefault();
                      handleSelect("insurance");
                    }
                  }}
                >
                  <div className="aicp-card-header">
                    <div className="aicp-card-icon-title">
                      <InsuranceShieldIcon size={18} color={"#AB8D5C"} />
                      <span className="aicp-card-title">{_t("insurance")}</span>
                    </div>
                    <div
                      className={`aicp-radio${selection === "insurance" ? " aicp-radio--ins" : ""}`}
                    />
                  </div>
                  <p className="aicp-card-desc">
                    {_t("insuranceCardDescription")}
                  </p>
                  <div className="aicp-card-footer-row aicp-insurance-price-row">
                    {renderInsuranceCoverage()}
                    {renderInsurancePricing(pricingPreview?.insurance)}
                  </div>
                </div>
              </div>

              {selection === "insurance" && (
                <div className="aicp-next-box aicp-next-box--ins">
                  <p className="aicp-next-label">{_t("whatHappensNext")}</p>
                  <ol className="aicp-next-steps">
                    <li>
                      <span className="aicp-step-num aicp-step-num--ins">
                        1
                      </span>
                      {_t("insuranceAwardStep1")}
                    </li>
                    <li>
                      <span className="aicp-step-num aicp-step-num--ins">
                        2
                      </span>
                      {_t("insuranceAwardStep2")}
                    </li>
                    <li>
                      <span className="aicp-step-num aicp-step-num--ins">
                        3
                      </span>
                      {_t("insuranceAwardStep3")}
                    </li>
                  </ol>
                </div>
              )}

              <p className="aicp-hint">{_t("selectOneOrLeaveUnselected")}</p>
            </>
          ))}
      </Modal.Body>
      <Modal.Footer className="aicp-footer">
        <Button
          variant="secondary"
          size="lg"
          block
          onClick={onHide}
          disabled={isConfirming}
        >
          {_t("cancel")}
        </Button>
        <Button
          variant="primary"
          size="lg"
          block
          loading={isConfirming}
          disabled={skorProtectLoading || insuranceRetryLoading}
          onClick={handleConfirm}
          iconStart={
            (selection === "insurance" || selection === "skor_protect") && (
              <Icon name="shield" size={16} />
            )
          }
        >
          {isConfirming
            ? _t("processing")
            : selection === "insurance" || selection === "skor_protect"
              ? _t("continue")
              : _t("awardProperty")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AwardInsuranceConfirmPopup;
